import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RestrictedAccessService } from './restricted-access.service';
import { ForcedUpdateService } from './forced-update.service';
import {
  NutritionAmbitionApiService,
  Request,
  AccountResponse,
  ChangePasswordRequest,
  Response,
  RegisterAccountRequest,
  CreateAuthHandoffTokenResponse,
  ValidateHandoffRequest,
  ValidateHandoffResponse,
  GetChatMessagesRequest,
  ChatMessagesResponse,
  ShowRestrictedMessageRequest,
  ShowLoggingExampleRequest,
  GetDetailedSummaryRequest,
  GetDetailedSummaryResponse,
  GetProfileAndTargetsRequest,
  GetProfileAndTargetsResponse,
  GetFavoritesRequest,
  GetFavoritesResponse,
  RelogFavoriteRequest,
  RelogFavoriteResponse,
  RemoveFavoriteRequest,
  RemoveFavoriteResponse,
  DeleteFoodEntryRequest,
  DeleteFoodEntryResponse,
  SubmitServingSelectionRequest,
  CancelServingSelectionRequest,
  EditFoodSelectionRequest,
  SubmitEditServingSelectionRequest,
  CancelEditSelectionRequest,
  SearchFoodPhraseRequest,
  SearchFoodPhraseResponse,
  HydrateAlternateSelectionRequest,
  GetInstantAlternativesRequest,
  GetInstantAlternativesResponse,
  UpdateMealSelectionRequest,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateSharedMealRequest,
  CreateSharedMealResponse,
  GetSharedMealRequest,
  GetSharedMealResponse,
  AccountInfoResponse,
  GetFeedbackResponse,
  MarkFeedbackAsShownRequest
} from './nutrition-ambition-api.service';

/**
 * Base response interface for checking blocked responses.
 */
interface BaseResponse {
  isSuccess?: boolean;
  isRestricted?: boolean;
  restrictedAccessPhase?: string;
  restrictedAccessRedirectUrl?: string;
  requiresAppUpdate?: boolean;
  minimumAppVersion?: string;
  updateMessage?: string;
}

/**
 * Facade service that wraps NutritionAmbitionApiService and handles:
 * - Forced app update checks (shows blocking modal)
 * - Restricted access handling (redirects to upgrade flow)
 *
 * All non-admin API calls should go through this service.
 * Returns null if the response was blocked.
 */
@Injectable({
  providedIn: 'root'
})
export class BackendService {

  constructor(
    private apiService: NutritionAmbitionApiService,
    private restrictedAccessService: RestrictedAccessService,
    private forcedUpdateService: ForcedUpdateService
  ) {}

  /**
   * Check response for blocking conditions.
   * @returns true if blocked (caller should stop processing)
   */
  private checkBlocked(response: BaseResponse): boolean {
    // Check forced update first
    if (this.forcedUpdateService.checkAndShow(response)) {
      return true;
    }
    // Check restricted access (use optional chaining for null safety)
    if (response?.isRestricted && response?.restrictedAccessPhase && response?.restrictedAccessRedirectUrl) {
      this.restrictedAccessService.handleRestrictedAccess(
        response.restrictedAccessPhase,
        response.restrictedAccessRedirectUrl
      );
      return true;
    }
    return false;
  }

  // ============================================================
  // Account Management
  // ============================================================

  async getAccountInfo(): Promise<AccountInfoResponse | null> {
    const response = await firstValueFrom(this.apiService.getAccountInfo());
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async getAccount(body?: Request): Promise<AccountResponse | null> {
    const response = await firstValueFrom(this.apiService.getAccount(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async changePassword(body?: ChangePasswordRequest): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.changePassword(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async registerAccount(body?: RegisterAccountRequest): Promise<AccountResponse | null> {
    const response = await firstValueFrom(this.apiService.registerAccount(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async cancelSubscription(): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.cancelSubscription());
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async deleteAccount(): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.deleteAccount());
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async deleteGuestAccount(): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.deleteGuestAccount());
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async dismissAppDownloadBanner(): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.dismissAppDownloadBanner());
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Auth
  // ============================================================

  async createAuthHandoffToken(redirectPath?: string): Promise<CreateAuthHandoffTokenResponse | null> {
    const response = await firstValueFrom(this.apiService.createAuthHandoffToken(redirectPath));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async validateHandoff(body?: ValidateHandoffRequest): Promise<ValidateHandoffResponse | null> {
    const response = await firstValueFrom(this.apiService.validateHandoff(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Feedback
  // ============================================================

  async getUnacknowledgedFeedback(body?: Request): Promise<GetFeedbackResponse | null> {
    const response = await firstValueFrom(this.apiService.getUnacknowledgedFeedback(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async markFeedbackAsShown(body?: MarkFeedbackAsShownRequest): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.markFeedbackAsShown(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Conversation / Chat
  // ============================================================

  async getChatMessages(body?: GetChatMessagesRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.getChatMessages(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async deleteMessage(messageId: string): Promise<void> {
    // void response - no blocking check possible
    await firstValueFrom(this.apiService.deleteMessage(messageId));
  }

  async showRestrictedMessage(body?: ShowRestrictedMessageRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.showRestrictedMessage(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async showLoggingExample(body?: ShowLoggingExampleRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.showLoggingExample(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Daily Summary / Profile
  // ============================================================

  async getDetailedSummary(body?: GetDetailedSummaryRequest): Promise<GetDetailedSummaryResponse | null> {
    const response = await firstValueFrom(this.apiService.getDetailedSummary(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async getProfileAndTargets(body?: GetProfileAndTargetsRequest): Promise<GetProfileAndTargetsResponse | null> {
    const response = await firstValueFrom(this.apiService.getProfileAndTargets(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Favorites
  // ============================================================

  async getFavorites(body?: GetFavoritesRequest): Promise<GetFavoritesResponse | null> {
    const response = await firstValueFrom(this.apiService.getFavorites(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async relogFavorite(body?: RelogFavoriteRequest): Promise<RelogFavoriteResponse | null> {
    const response = await firstValueFrom(this.apiService.relogFavorite(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async removeFavorite(body?: RemoveFavoriteRequest): Promise<RemoveFavoriteResponse | null> {
    const response = await firstValueFrom(this.apiService.removeFavorite(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Food Entry
  // ============================================================

  async deleteFoodEntry(body?: DeleteFoodEntryRequest): Promise<DeleteFoodEntryResponse | null> {
    const response = await firstValueFrom(this.apiService.deleteFoodEntry(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Food Selection / Serving
  // ============================================================

  async submitServingSelection(body?: SubmitServingSelectionRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.submitServingSelection(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async cancelFoodLogging(body?: CancelServingSelectionRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.cancelFoodLogging(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async startEditSelection(body?: EditFoodSelectionRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.startEditSelection(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async submitEditServingSelection(body?: SubmitEditServingSelectionRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.submitEditServingSelection(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async cancelEditSelection(body?: CancelEditSelectionRequest): Promise<ChatMessagesResponse | null> {
    const response = await firstValueFrom(this.apiService.cancelEditSelection(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Food Search
  // ============================================================

  async searchFoodPhrase(body?: SearchFoodPhraseRequest): Promise<SearchFoodPhraseResponse | null> {
    const response = await firstValueFrom(this.apiService.searchFoodPhrase(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async updateFoodPhrase(body?: SearchFoodPhraseRequest): Promise<SearchFoodPhraseResponse | null> {
    const response = await firstValueFrom(this.apiService.updateFoodPhrase(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async hydrateAlternateSelection(body?: HydrateAlternateSelectionRequest): Promise<SearchFoodPhraseResponse | null> {
    const response = await firstValueFrom(this.apiService.hydrateAlternateSelection(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async getInstantAlternatives(body?: GetInstantAlternativesRequest): Promise<GetInstantAlternativesResponse | null> {
    const response = await firstValueFrom(this.apiService.getInstantAlternatives(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async updateMealSelection(body?: UpdateMealSelectionRequest): Promise<Response | null> {
    const response = await firstValueFrom(this.apiService.updateMealSelection(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Payments
  // ============================================================

  async createCheckoutSession(body?: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse | null> {
    const response = await firstValueFrom(this.apiService.createCheckoutSession(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  // ============================================================
  // Sharing
  // ============================================================

  async createSharedMeal(body?: CreateSharedMealRequest): Promise<CreateSharedMealResponse | null> {
    const response = await firstValueFrom(this.apiService.createSharedMeal(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }

  async getSharedMeal(body?: GetSharedMealRequest): Promise<GetSharedMealResponse | null> {
    const response = await firstValueFrom(this.apiService.getSharedMeal(body));
    if (this.checkBlocked(response)) return null;
    return response;
  }
}
